import { CoreArchiveDialog } from "@/components/core-archive-dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Plus, Archive, AlertTriangle, ChevronDown, CalendarIcon, MapPin } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertAccountSchema, insertClientGroupSchema, type Account, type ClientGroup, type Partner, type Deal, type Contact } from "@shared/schema";
import { PartnerFormModal } from "@/components/partner-form-modal";
import { ContactFormModal } from "@/components/contact-form-modal";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { AccountSelect } from "@/components/form-fields/account-select";
import { AddressInput, cleanupPacContainers } from "@/components/form-fields/address-input";
import { z } from "zod";
import { useLocation } from "wouter";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatABN, formatACN, stripSpaces } from "@/utils/formatters";
import { DealFormModal } from "@/components/deal-form-modal";

// Address data interface matching AddressInput component
interface AddressData {
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: string;
  longitude?: string;
  placeId?: string;
  isVerified?: number;
}

// Create a pre-validation schema for required fields that runs before the main schema
const accountFormPreValidation = z.object({
  clientGroupId: z.string().min(1, "Client group is required"),
  legalName: z.string().min(1, "Legal name is required").max(255, "Legal name must be less than 255 characters"),
  entityType: z.enum(["company", "trust", "partnership", "sole_trader"], {
    required_error: "Entity type is required",
  }),
}).passthrough();

// Combine pre-validation with the main schema
const accountFormSchema = accountFormPreValidation.pipe(insertAccountSchema);

type AccountFormData = z.infer<typeof insertAccountSchema>;
type ClientGroupFormData = z.infer<typeof insertClientGroupSchema>;

const INDUSTRIES = [
  { value: "agriculture_forestry_fishing", label: "Agriculture, Forestry & Fishing" },
  { value: "mining", label: "Mining" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "construction", label: "Construction" },
  { value: "retail_trade", label: "Retail Trade" },
  { value: "wholesale_trade", label: "Wholesale Trade" },
  { value: "accommodation_food_services", label: "Accommodation & Food Services" },
  { value: "transport_postal_warehousing", label: "Transport, Postal & Warehousing" },
  { value: "information_media_telecommunications", label: "Information, Media & Telecommunications" },
  { value: "financial_insurance_services", label: "Financial & Insurance Services" },
  { value: "asset_holding", label: "Asset Holding" },
  { value: "rental_hiring_real_estate_services", label: "Rental, Hiring & Real Estate Services" },
  { value: "professional_scientific_technical_services", label: "Professional, Scientific & Technical Services" },
  { value: "administrative_support_services", label: "Administrative & Support Services" },
  { value: "public_administration_safety", label: "Public Administration & Safety" },
  { value: "education_training", label: "Education & Training" },
  { value: "health_care_social_assistance", label: "Health Care & Social Assistance" },
  { value: "arts_recreation_services", label: "Arts & Recreation Services" },
  { value: "electricity_gas_water_waste", label: "Electricity, Gas, Water & Waste Services" },
  { value: "other_services", label: "Other Services" },
];

interface AccountFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account?: Account;
  defaultClientGroupId?: string;
  defaultEntityType?: "company" | "trust" | "partnership" | "sole_trader";
  onAccountCreated?: (account: Account) => void;
  disableNesting?: boolean; // Prevent nested account creation dialogs
}

export function AccountFormModal({
  open,
  onOpenChange,
  account,
  defaultClientGroupId,
  defaultEntityType,
  onAccountCreated,
  disableNesting = false,
}: AccountFormModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const isEdit = !!account;
  const isGroupContext = !!defaultClientGroupId; // Detect if modal opened from client group context
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [showCreatePartnerDialog, setShowCreatePartnerDialog] = useState(false);
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  const [showCreateTrusteeAccountDialog, setShowCreateTrusteeAccountDialog] = useState(false);
  const [archivePending, setArchivePending] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [selectedIndividualTrusteeId, setSelectedIndividualTrusteeId] = useState<string | null>(null);
  const [selectedCorporateTrusteeId, setSelectedCorporateTrusteeId] = useState<string | null>(null);
  const [selectedCorporateTrusteeAccount, setSelectedCorporateTrusteeAccount] = useState<Account | null>(null);

  // Tab state (F007)
  const [activeTab, setActiveTab] = useState<string>("details");
  const [showDealFormModal, setShowDealFormModal] = useState(false);

  // Address integration state (ISSUE-054)
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [useManualAddressEntry, setUseManualAddressEntry] = useState(false);
  const [hasGoogleMapsApi, setHasGoogleMapsApi] = useState(false);
  const [googleApiError, setGoogleApiError] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    unitNumber: "",
    streetNumber: "",
    streetName: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "Australia",
  });

  // Check for Google Maps API on mount
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    setHasGoogleMapsApi(!!apiKey);
    if (!apiKey) {
      setUseManualAddressEntry(true);
    }
  }, []);

  // ISSUE-054 Rework #2: Handle Google API errors by auto-switching to manual entry
  const handleGoogleApiError = () => {
    setGoogleApiError(true);
    setUseManualAddressEntry(true);
  };

  // Fetch client groups for dropdown
  const { data: clientGroups, isLoading: isLoadingGroups, error: groupsError } = useQuery<ClientGroup[]>({
    queryKey: ["/api/crm/client-groups"],
  });

  // Fetch partners for dropdown
  const { data: partnersData } = useQuery<Partner[]>({
    queryKey: ["/api/crm/partners"],
    enabled: open,
  });
  const partners = Array.isArray(partnersData) ? partnersData : [];

  // Find currently linked partner
  const linkedPartner = isEdit && account?.partnerId
    ? partners.find((p) => p.id === account.partnerId)
    : null;

  // Fetch deals linked to this account for its Deals tab
  const { data: deals = [], isLoading: isLoadingDeals } = useQuery<Deal[]>({
    queryKey: ['/api/crm/deals', { applicantId: account?.id, applicantType: 'account' }],
    enabled: !!account?.id && isEdit,
  });

  // Form for creating new client group
  const createGroupForm = useForm<ClientGroupFormData>({
    resolver: zodResolver(insertClientGroupSchema),
    defaultValues: {
      groupName: "",
    },
  });

  const defaultValues = {
    clientGroupId: defaultClientGroupId || "",
    legalName: "",
    tradingName: "",
    entityType: (defaultEntityType || "company") as "company" | "trust" | "partnership" | "sole_trader",
    abn: "",
    acn: "",
    industry: undefined, // ✅ FIXED: Use undefined instead of "" for optional enum field
    anzsicCode: "",
    annualRevenue: null as string | null,
    employeeCount: null as number | null,
    website: "",
    linkedin: "",
    email: "",
    phone: "",
    streetAddress: "",
    suburb: "",
    state: "",
    postcode: "",
    notes: "",
    partnerId: null as string | null,
  };

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountFormSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (account) {
        // Only reset with form-relevant fields to avoid Zod strict mode validation errors
        // Database-only fields (id, createdAt, updatedAt, createdBy) cause silent validation failures
        form.reset({
          clientGroupId: account.clientGroupId,
          entityType: account.entityType,
          legalName: account.legalName,
          tradingName: account.tradingName ?? "",
          abn: account.abn ?? "",
          abnRegistrationDate: account.abnRegistrationDate ?? undefined,
          gstRegistrationDate: account.gstRegistrationDate ?? undefined,
          acn: account.acn ?? "",
          acnStartDate: account.acnStartDate ?? undefined,
          companyClass: account.companyClass ?? undefined,
          trustType: account.trustType ?? "",
          trusteeType: account.trusteeType ?? "",
          trustDeedDate: account.trustDeedDate ?? undefined,
          smsfComplyingStatus: account.smsfComplyingStatus ?? undefined,
          smsfAtoCheckedAt: account.smsfAtoCheckedAt ?? undefined,
          linkedSmsfId: account.linkedSmsfId ?? undefined,
          partnershipType: account.partnershipType ?? undefined,
          industry: account.industry ?? undefined,
          annualRevenue: account.annualRevenue ?? null,
          employeeCount: account.employeeCount ?? null,
          email: account.email ?? "",
          phone: account.phone ?? "",
          streetAddress: account.streetAddress ?? "",
          suburb: account.suburb ?? "",
          state: account.state ?? "",
          postcode: account.postcode ?? "",
          notes: account.notes ?? "",
          partnerId: account.partnerId ?? null,
          website: account.website ?? "",
          linkedin: account.linkedin ?? "",
          anzsicCode: account.anzsicCode ?? "",
        });
        // Initialize selected partner from linked partner
        setSelectedPartnerId(linkedPartner?.id || null);
      } else {
        form.reset(defaultValues);
        setSelectedPartnerId(null);

        // Pre-fill client group when context provided
        if (defaultClientGroupId) {
          form.setValue("clientGroupId", defaultClientGroupId, {
            shouldValidate: true,
            shouldDirty: false,
            shouldTouch: true
          });
        }
      }
    }
  }, [open, account, form, linkedPartner, defaultClientGroupId]);

  const handleOpenChange = (newOpen: boolean) => {
    if (archivePending) return;
    if (!newOpen) {
      form.reset(defaultValues);
      setSelectedIndividualTrusteeId(null);
      setSelectedCorporateTrusteeId(null);
      setSelectedCorporateTrusteeAccount(null);
      // Reset address state (ISSUE-054)
      setAddressData(null);
      setGoogleApiError(false); // Reset API error state on close
      setManualAddress({
        unitNumber: "",
        streetNumber: "",
        streetName: "",
        suburb: "",
        state: "",
        postcode: "",
        country: "Australia",
      });
      if (hasGoogleMapsApi) {
        setUseManualAddressEntry(false);
      }
      // ISSUE-054 Rework #3: Clean up pac-containers when modal closes
      cleanupPacContainers();
    }
    onOpenChange(newOpen);
  };

  const createMutation = useMutation({
    mutationFn: async (data: AccountFormData) => {
      const response = await apiRequest("POST", "/api/crm/accounts", data);
      return response.json();
    },
    onSuccess: async (newAccount: Account, variables: AccountFormData) => {
      // If trust with individual trustee, create account_people relationship
      if (variables.entityType === "trust" && variables.trusteeType === "individual" && selectedIndividualTrusteeId) {
        try {
          await apiRequest("POST", `/api/crm/accounts/${newAccount.id}/people`, {
            contactId: selectedIndividualTrusteeId,
            role: "trustee",
          });
        } catch (error: any) {
          toast({
            title: "Warning",
            description: `Account created but failed to link individual trustee: ${error.message}`,
            variant: "destructive",
          });
        }
      }

      // If trust with corporate trustee, create trustee_companies relationship
      if (variables.entityType === "trust" && variables.trusteeType === "corporate" && selectedCorporateTrusteeId) {
        try {
          await apiRequest("POST", `/api/crm/accounts/${newAccount.id}/trustees/corporate`, {
            trusteeAccountId: selectedCorporateTrusteeId,
          });
        } catch (error: any) {
          toast({
            title: "Warning",
            description: `Account created but failed to link corporate trustee: ${error.message}`,
            variant: "destructive",
          });
        }
      }

      // ISSUE-054: Create address record and link to account if address data provided
      const hasAddressData = addressData || (useManualAddressEntry && manualAddress.streetName);
      if (hasAddressData) {
        try {
          // Prepare address data from either Google autocomplete or manual entry
          const addressPayload = addressData || {
            unitNumber: manualAddress.unitNumber || undefined,
            streetNumber: manualAddress.streetNumber || undefined,
            streetName: manualAddress.streetName || undefined,
            suburb: manualAddress.suburb || undefined,
            state: manualAddress.state || undefined,
            postcode: manualAddress.postcode || undefined,
            country: manualAddress.country || "Australia",
            formattedAddress: buildFormattedAddress(),
            isVerified: 0,
          };

          // Create address record
          const addressResponse = await apiRequest("POST", "/api/crm/addresses", addressPayload);
          const newAddress = await addressResponse.json();

          // Link address to account via junction table
          await apiRequest("POST", `/api/crm/accounts/${newAccount.id}/addresses`, {
            addressId: newAddress.id,
            addressType: "principal_place_of_business",
            isPrimary: 1,
          });
        } catch (error: any) {
          toast({
            title: "Warning",
            description: `Account created but failed to save address: ${error.message}`,
            variant: "destructive",
          });
        }
      }

      queryClient.refetchQueries({ queryKey: ["/api/crm/accounts"] });
      toast({
        title: "Account created",
        description: "The account has been created successfully.",
      });
      onOpenChange(false);
      form.reset(defaultValues);

      // Call callback if provided (nested creation)
      if (onAccountCreated) {
        onAccountCreated(newAccount);
      } else {
        // Auto-navigate to account detail page (if not nested creation)
        navigate(`/accounts/${newAccount.id}`);
      }
    },
    onError: (error: Error) => {
      // Check for ABN uniqueness constraint violation
      const isAbnDuplicate = error.message.includes("crm_x_accounts_abn_unique_idx") ||
                             error.message.includes("duplicate key") ||
                             error.message.includes("unique constraint");

      toast({
        title: "Error",
        description: isAbnDuplicate
          ? "An account with this ABN already exists. Please check the ABN and try again."
          : error.message,
        variant: "destructive",
      });

      if (isAbnDuplicate) {
        form.setError("abn", {
          type: "manual",
          message: "This ABN is already registered to another account",
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: AccountFormData) =>
      apiRequest("PATCH", `/api/crm/accounts/${account?.id}`, data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/accounts"] });
      toast({
        title: "Account updated",
        description: "The account has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      // Check for ABN uniqueness constraint violation
      const isAbnDuplicate = error.message.includes("crm_x_accounts_abn_unique_idx") ||
                             error.message.includes("duplicate key") ||
                             error.message.includes("unique constraint");

      toast({
        title: "Error",
        description: isAbnDuplicate
          ? "An account with this ABN already exists. Please check the ABN and try again."
          : error.message,
        variant: "destructive",
      });

      if (isAbnDuplicate) {
        form.setError("abn", {
          type: "manual",
          message: "This ABN is already registered to another account",
        });
      }
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: async (data: ClientGroupFormData): Promise<ClientGroup> => {
      const response = await apiRequest("POST", "/api/crm/client-groups", data);
      return response.json();
    },
    onSuccess: (newGroup: ClientGroup) => {
      queryClient.setQueryData<ClientGroup[]>(["/api/crm/client-groups"], (oldData) => {
        if (!oldData) return [newGroup];
        const exists = oldData.some(g => g.id === newGroup.id);
        return exists ? oldData : [...oldData, newGroup];
      });
      form.setValue("clientGroupId", newGroup.id, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      setShowCreateGroupDialog(false);
      createGroupForm.reset();
      toast({
        title: "Success",
        description: "Client group created and selected",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create client group",
        variant: "destructive",
      });
    },
  });






  const handlePartnerChange = (partnerId: string) => {
    const newPartnerId = partnerId === "none" ? null : partnerId;
    setSelectedPartnerId(newPartnerId);

    // Update the form value so it persists when the form is saved
    form.setValue("partnerId", newPartnerId);

    // Don't call immediate mutation - let the form save handle it
    // This prevents the form from being reset when the query refetches
  };

  const handlePartnerCreated = (newPartner: Partner) => {
    // Update cache to include new partner
    queryClient.setQueryData<Partner[]>(["/api/crm/partners"], (oldData) => {
      if (!oldData) return [newPartner];
      const exists = oldData.some(p => p.id === newPartner.id);
      return exists ? oldData : [...oldData, newPartner];
    });

    // Set selected partner ID
    setSelectedPartnerId(newPartner.id);

    // Update the form value
    form.setValue("partnerId", newPartner.id);

    // Don't call immediate mutation - let the form save handle it
    // When creating a new account, partnerId will be included in the create payload
    // When editing, the form submit will save the partnerId

    setShowCreatePartnerDialog(false);
  };

  const handleContactCreated = (newContact: Contact) => {
    // Update cache to include new contact
    const clientGroupId = form.getValues("clientGroupId");
    if (clientGroupId) {
      queryClient.setQueryData<Contact[]>(["/api/crm/contacts", { clientGroupId }], (oldData) => {
        if (!oldData) return [newContact];
        const exists = oldData.some(c => c.id === newContact.id);
        return exists ? oldData : [...oldData, newContact];
      });
    }
    queryClient.setQueryData<Contact[]>(["/api/crm/contacts"], (oldData) => {
      if (!oldData) return [newContact];
      const exists = oldData.some(c => c.id === newContact.id);
      return exists ? oldData : [...oldData, newContact];
    });

    // Set selected individual trustee ID
    setSelectedIndividualTrusteeId(newContact.id);

    setShowCreateContactDialog(false);

    toast({
      title: "Success",
      description: "Contact created and selected as individual trustee",
    });
  };

  const handleTrusteeAccountCreated = (newAccount: Account) => {
    // Update cache to include new account
    const clientGroupId = form.getValues("clientGroupId");
    if (clientGroupId) {
      queryClient.setQueryData<Account[]>(["/api/crm/accounts", { clientGroupId }], (oldData) => {
        if (!oldData) return [newAccount];
        const exists = oldData.some(a => a.id === newAccount.id);
        return exists ? oldData : [...oldData, newAccount];
      });
    }
    queryClient.setQueryData<Account[]>(["/api/crm/accounts"], (oldData) => {
      if (!oldData) return [newAccount];
      const exists = oldData.some(a => a.id === newAccount.id);
      return exists ? oldData : [...oldData, newAccount];
    });

    // Set selected corporate trustee ID
    setSelectedCorporateTrusteeId(newAccount.id);
    setSelectedCorporateTrusteeAccount(newAccount);

    setShowCreateTrusteeAccountDialog(false);

    toast({
      title: "Success",
      description: "Corporate trustee created and selected",
    });
  };

  // ISSUE-054: Helper to build formatted address from manual entry
  const buildFormattedAddress = () => {
    const parts: string[] = [];

    // Build street line with proper handling of optional fields
    const streetParts: string[] = [];
    if (manualAddress.unitNumber?.trim()) {
      streetParts.push(`${manualAddress.unitNumber.trim()}/`);
    }
    if (manualAddress.streetNumber?.trim()) {
      streetParts.push(manualAddress.streetNumber.trim());
    }
    if (manualAddress.streetName?.trim()) {
      if (streetParts.length > 0) {
        streetParts.push(` ${manualAddress.streetName.trim()}`);
      } else {
        streetParts.push(manualAddress.streetName.trim());
      }
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(""));
    }

    // Build location line
    const locationParts = [
      manualAddress.suburb?.trim(),
      manualAddress.state?.trim(),
      manualAddress.postcode?.trim(),
    ].filter(Boolean);

    if (locationParts.length > 0) {
      parts.push(locationParts.join(" "));
    }

    // Add country only if not Australia
    if (manualAddress.country?.trim() && manualAddress.country.trim().toLowerCase() !== "australia") {
      parts.push(manualAddress.country.trim());
    }

    return parts.join(", ");
  };

  // ISSUE-054: Handle address selection from Google autocomplete
  const handleAddressSelect = (data: AddressData) => {
    setAddressData(data);
    setUseManualAddressEntry(false);
  };

  const onSubmit = (data: AccountFormData) => {
    const payload = {
      ...data,
      partnerId: selectedPartnerId || null,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending || createGroupMutation.isPending;
  const canSubmit = !isPending && !isLoadingGroups && clientGroups && clientGroups.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-account-form"
        onInteractOutside={(e) => {
          // Prevent dialog from closing when clicking Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Account" : "Add Account"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update account information"
              : "Create a new account record"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {isEdit && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                  <TabsTrigger value="deals" data-testid="tab-deals">Deals</TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {(!isEdit || activeTab === "details") && (
            <div className="space-y-4 mt-4">
            {/* Client Group */}
            <FormField
              control={form.control}
              name="clientGroupId"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Client Group *</FormLabel>
                    {!isGroupContext && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCreateGroupDialog(true)}
                        disabled={createGroupMutation.isPending}
                        data-testid="button-create-client-group"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        New Group
                      </Button>
                    )}
                  </div>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                    disabled={isLoadingGroups || isGroupContext}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-client-group">
                        <SelectValue
                          placeholder={
                            isLoadingGroups
                              ? "Loading client groups..."
                              : groupsError
                                ? "Error loading client groups"
                                : "Select client group"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {clientGroups && clientGroups.length > 0 ? (
                        clientGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.groupName}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-groups" disabled>
                          No client groups available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {isGroupContext
                      ? "This account will be created in the current client group"
                      : "Every account must belong to a client group"}
                  </FormDescription>
                  {groupsError && (
                    <p className="text-sm text-destructive">
                      Failed to load client groups. Please try again.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Referring Partner */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Referring Partner (Optional)
                </label>
                {!isGroupContext && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreatePartnerDialog(true)}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    New Partner
                  </Button>
                )}
              </div>
              <Select
                onValueChange={handlePartnerChange}
                value={selectedPartnerId || undefined}
                disabled={isGroupContext}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select partner (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No partner</SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.partnerName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                {isGroupContext
                  ? "Referring partner is inherited from the client group context"
                  : "Link this account to a referring partner"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="legalName"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Legal Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="ABC Manufacturing Pty Ltd"
                        data-testid="input-legal-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tradingName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trading Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="ABC Mfg"
                        data-testid="input-trading-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="entityType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Entity Type *</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        const previousType = field.value;
                        field.onChange(value);

                        // Clear entity-specific fields when type changes
                        if (previousType !== value) {
                          if (previousType === "company" && value !== "company") {
                            // Clearing company fields
                            form.setValue("acn", "");
                          }
                          if (previousType === "trust" && value !== "trust") {
                            // Clearing trust fields
                            form.setValue("trustType", "");
                            form.setValue("trusteeType", "");
                          }
                        }
                      }}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-entity-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="trust">Trust</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="sole_trader">Sole Trader</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="abn"
                render={({ field }) => {
                  const abnValue = field.value || "";
                  const abnDigits = abnValue.replace(/\s/g, "");
                  const abnLength = abnDigits.length;
                  const abnIsValid = abnDigits === "" || (abnLength === 11 && /^\d+$/.test(abnDigits));

                  // Format for display (F007: Format as XX XXX XXX XXX)
                  const displayValue = formatABN(field.value);

                  return (
                    <FormItem>
                      <FormLabel>ABN</FormLabel>
                      <FormControl>
                        <Input
                          value={displayValue}
                          placeholder="12345678901"
                          data-testid="input-abn"
                          onChange={(e) => {
                            // Strip spaces immediately on input (store raw digits)
                            const rawValue = e.target.value;
                            const strippedValue = stripSpaces(rawValue);
                            field.onChange(strippedValue);
                            form.trigger("abn");
                          }}
                          onBlur={(e) => {
                            // Re-format on blur for consistent display
                            const strippedValue = stripSpaces(e.target.value);
                            field.onChange(strippedValue);
                            form.trigger("abn");
                          }}
                          onPaste={(e) => {
                            // Handle paste event to strip spaces from pasted content
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData("text");
                            const strippedValue = stripSpaces(pastedText);
                            field.onChange(strippedValue);
                            form.trigger("abn");
                          }}
                        />
                      </FormControl>
                      {abnValue && !abnIsValid && (
                        <p className="text-sm text-destructive">
                          ABN must be exactly 11 digits (currently {abnLength} digits)
                        </p>
                      )}
                      <FormDescription className="text-xs">
                        Enter 11 digits. Spaces are automatically removed.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {/* ABN Registration Date - Only for Edit Mode */}
              {isEdit && form.watch("abn") && (
                <FormField
                  control={form.control}
                  name="abnRegistrationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ABN Registration Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                              data-testid="input-abn-registration-date"
                            >
                              {field.value ? (
                                format(new Date(field.value), "dd/MM/yyyy")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value ? new Date(field.value) : undefined}
                            onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : null)}
                            disabled={(date) => date > new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* GST Registration Date - Only for Edit Mode (F007: AC003/AC004) */}
              {isEdit && (
                <FormField
                  control={form.control}
                  name="gstRegistrationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GST Registration Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                              data-testid="input-gst-registration-date"
                            >
                              {field.value ? (
                                format(new Date(field.value), "dd/MM/yyyy")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value ? new Date(field.value) : undefined}
                            onSelect={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : null)}
                            disabled={(date) => date > new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* ACN - Only for Companies */}
              {form.watch("entityType") === "company" && (
                <FormField
                  control={form.control}
                  name="acn"
                  render={({ field }) => {
                    const acnValue = field.value || "";
                    const acnDigits = acnValue.replace(/\s/g, "");
                    const acnLength = acnDigits.length;
                    const acnIsValid = acnDigits === "" || (acnLength === 9 && /^\d+$/.test(acnDigits));

                    // Format for display (F007: Format as XXX XXX XXX)
                    const displayValue = formatACN(field.value);

                    return (
                      <FormItem>
                        <FormLabel>ACN</FormLabel>
                        <FormControl>
                          <Input
                            value={displayValue}
                            placeholder="123456789"
                            data-testid="input-acn"
                            onChange={(e) => {
                              // Strip spaces immediately on input (store raw digits)
                              const rawValue = e.target.value;
                              const strippedValue = stripSpaces(rawValue);
                              field.onChange(strippedValue);
                              form.trigger("acn");
                            }}
                            onBlur={(e) => {
                              // Re-format on blur for consistent display
                              const strippedValue = stripSpaces(e.target.value);
                              field.onChange(strippedValue);
                              form.trigger("acn");
                            }}
                            onPaste={(e) => {
                              // Handle paste event to strip spaces from pasted content
                              e.preventDefault();
                              const pastedText = e.clipboardData.getData("text");
                              const strippedValue = stripSpaces(pastedText);
                              field.onChange(strippedValue);
                              form.trigger("acn");
                            }}
                          />
                        </FormControl>
                        {acnValue && !acnIsValid && (
                          <p className="text-sm text-destructive">
                            ACN must be exactly 9 digits (currently {acnLength} digits)
                          </p>
                        )}
                        <FormDescription className="text-xs">
                          Enter 9 digits. Spaces are automatically removed.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}

              {/* Company Class - Only for Companies */}
              {form.watch("entityType") === "company" && (
                <FormField
                  control={form.control}
                  name="companyClass"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Class</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-company-class">
                            <SelectValue placeholder="Select company class" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="proprietary_limited">Proprietary Limited</SelectItem>
                          <SelectItem value="public_limited">Public Limited</SelectItem>
                          <SelectItem value="foreign_company">Foreign Company</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Trust Structure — Only for Trusts (#459: visual hierarchy) */}
              {form.watch("entityType") === "trust" && (
                <div className="col-span-2 space-y-4" data-testid="trust-structure-section">
                  {/* Trust layer */}
                  <div className="rounded-md border border-border p-4 space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trust</p>
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="trustType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Trust Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || undefined}
                            >
                              <FormControl>
                                <SelectTrigger data-testid="select-trust-type">
                                  <SelectValue placeholder="Select trust type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="discretionary">Discretionary</SelectItem>
                                <SelectItem value="unit">Unit Trust</SelectItem>
                                <SelectItem value="family">Family Trust</SelectItem>
                                <SelectItem value="hybrid">Hybrid Trust</SelectItem>
                                <SelectItem value="bare_trust">Bare Trust</SelectItem>
                                <SelectItem value="smsf">SMSF</SelectItem>
                                <SelectItem value="testamentary">Testamentary Trust</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="trusteeType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Trustee Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || undefined}
                            >
                              <FormControl>
                                <SelectTrigger data-testid="select-trustee-type">
                                  <SelectValue placeholder="Select trustee type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="individual">Individual</SelectItem>
                                <SelectItem value="corporate">Corporate</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Trustee details layer — indented to show Trust → Trustee relationship */}
                  {!disableNesting && form.watch("trusteeType") === "individual" && (
                    <div className="ml-4 rounded-md border border-border bg-muted/30 p-4 space-y-3" data-testid="trustee-details-section">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trustee — Individual</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Individual Trustee
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCreateContactDialog(true)}
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          New Contact
                        </Button>
                      </div>
                      <ContactSelect
                        value={selectedIndividualTrusteeId}
                        onValueChange={setSelectedIndividualTrusteeId}
                        placeholder="Select individual trustee"
                        clientGroupId={form.watch("clientGroupId")}
                        data-testid="select-individual-trustee"
                      />
                      <p className="text-sm text-muted-foreground">
                        Select an existing contact or create a new one as individual trustee
                      </p>
                    </div>
                  )}

                  {/* Corporate Trustee layer — indented with prominent name + ACN display */}
                  {!disableNesting && form.watch("trusteeType") === "corporate" && (
                    <div className="ml-4 rounded-md border border-border bg-muted/30 p-4 space-y-3" data-testid="trustee-details-section">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trustee — Corporate</p>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Corporate Trustee
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCreateTrusteeAccountDialog(true)}
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          New Company
                        </Button>
                      </div>
                      <AccountSelect
                        value={selectedCorporateTrusteeId}
                        onValueChange={(value) => {
                          setSelectedCorporateTrusteeId(value);
                          if (selectedCorporateTrusteeAccount?.id !== value) {
                            setSelectedCorporateTrusteeAccount(null);
                          }
                        }}
                        placeholder="Select corporate trustee (company)"
                        clientGroupId={form.watch("clientGroupId")}
                        entityTypeFilter="company"
                        selectedAccount={selectedCorporateTrusteeAccount}
                        data-testid="select-corporate-trustee"
                      />
                      <p className="text-sm text-muted-foreground">
                        Select an existing company or create a new one as corporate trustee
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Partnership Type - Only for Partnerships */}
              {form.watch("entityType") === "partnership" && (
                <FormField
                  control={form.control}
                  name="partnershipType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Partnership Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-partnership-type">
                            <SelectValue placeholder="Select partnership type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="general">General Partnership</SelectItem>
                          <SelectItem value="limited">Limited Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* SMSF Complying Status - Only for SMSF Trusts */}
              {form.watch("entityType") === "trust" && form.watch("trustType") === "smsf" && (
                <FormField
                  control={form.control}
                  name="smsfComplyingStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SMSF Complying Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-smsf-complying-status">
                            <SelectValue placeholder="Select complying status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="complying">Complying</SelectItem>
                          <SelectItem value="non_complying">Non-Complying</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-industry">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent position="popper" className="max-h-[300px] overflow-y-auto" sideOffset={5}>
                        {INDUSTRIES.map((industry) => (
                          <SelectItem key={industry.value} value={industry.value}>
                            {industry.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="anzsicCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ANZSIC Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="e.g., 6221"
                        data-testid="input-anzsic-code"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Australian and New Zealand Standard Industrial Classification code
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="annualRevenue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Annual Revenue</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="e.g., 1500000"
                        type="number"
                        min="0"
                        step="1000"
                        data-testid="input-annual-revenue"
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === "" ? null : value);
                        }}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Annual revenue in AUD
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="employeeCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employee Count</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="e.g., 25"
                        type="number"
                        min="0"
                        step="1"
                        data-testid="input-employee-count"
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === "" ? null : parseInt(value, 10));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="https://www.example.com.au"
                        type="url"
                        data-testid="input-website"
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val && !/^https?:\/\//i.test(val)) {
                            field.onChange(`https://${val}`);
                          }
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="linkedin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="https://www.linkedin.com/company/example"
                        type="url"
                        data-testid="input-linkedin"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="contact@example.com"
                        type="email"
                        data-testid="input-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="(02) 1234 5678"
                        type="tel"
                        data-testid="input-phone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address Section - Collapsible with Google Maps Integration (ISSUE-054) */}
              <div className="col-span-2">
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-between mb-2"
                    >
                      <div className="flex items-center text-sm font-medium">
                        <MapPin className="h-4 w-4 mr-2" />
                        Principal Place of Business (Optional)
                      </div>
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4">
                    {/* Toggle between search and manual entry */}
                    {hasGoogleMapsApi && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="manualAddressEntry"
                          checked={useManualAddressEntry}
                          onChange={(e) => {
                            setUseManualAddressEntry(e.target.checked);
                            if (e.target.checked) {
                              setAddressData(null);
                            }
                          }}
                          className="rounded border-gray-300"
                          data-testid="checkbox-manual-address-entry"
                        />
                        <Label htmlFor="manualAddressEntry" className="cursor-pointer text-sm">
                          Enter address manually
                        </Label>
                      </div>
                    )}

                    {useManualAddressEntry ? (
                      <div className="space-y-3">
                        {/* Show info message if switched due to API error */}
                        {googleApiError && (
                          <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            Google Maps unavailable. Please enter address manually.
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Unit Number</Label>
                            <Input
                              value={manualAddress.unitNumber}
                              onChange={(e) => setManualAddress({ ...manualAddress, unitNumber: e.target.value })}
                              placeholder="e.g., 5"
                              data-testid="input-unit-number"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Street Number</Label>
                            <Input
                              value={manualAddress.streetNumber}
                              onChange={(e) => setManualAddress({ ...manualAddress, streetNumber: e.target.value })}
                              placeholder="e.g., 123"
                              data-testid="input-street-number"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Street Name</Label>
                          <Input
                            value={manualAddress.streetName}
                            onChange={(e) => setManualAddress({ ...manualAddress, streetName: e.target.value })}
                            placeholder="e.g., Main Street"
                            data-testid="input-street-name"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Suburb</Label>
                            <Input
                              value={manualAddress.suburb}
                              onChange={(e) => setManualAddress({ ...manualAddress, suburb: e.target.value })}
                              placeholder="e.g., Sydney"
                              data-testid="input-suburb"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">State</Label>
                            <Select
                              value={manualAddress.state || undefined}
                              onValueChange={(value) => setManualAddress({ ...manualAddress, state: value })}
                            >
                              <SelectTrigger data-testid="select-state">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="NSW">NSW</SelectItem>
                                <SelectItem value="VIC">VIC</SelectItem>
                                <SelectItem value="QLD">QLD</SelectItem>
                                <SelectItem value="WA">WA</SelectItem>
                                <SelectItem value="SA">SA</SelectItem>
                                <SelectItem value="TAS">TAS</SelectItem>
                                <SelectItem value="ACT">ACT</SelectItem>
                                <SelectItem value="NT">NT</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Postcode</Label>
                            <Input
                              value={manualAddress.postcode}
                              onChange={(e) => setManualAddress({ ...manualAddress, postcode: e.target.value })}
                              placeholder="e.g., 2000"
                              maxLength={4}
                              data-testid="input-postcode"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>Search Address</Label>
                        <AddressInput
                          onAddressSelect={handleAddressSelect}
                          onApiError={handleGoogleApiError}
                          placeholder="Start typing to search..."
                          data-testid="input-address-search"
                        />
                        {addressData && (
                          <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {addressData.formattedAddress || "Address selected"}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Address will be saved to the address book and linked to this account
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value || ""}
                        placeholder="Additional information..."
                        data-testid="input-notes"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Record status Section - RULE-SAFETY-001 */}
            {isEdit && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-between text-destructive hover:text-destructive hover:bg-destructive/10 mt-6"
                    data-testid="button-danger-zone-toggle"
                  >
                    <div className="flex items-center">
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Record status
                    </div>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  <Alert>
                    <AlertDescription className="text-sm">
                      Archive hides this record from active lists. Its linked records and history stay available. You can restore it later.
                    </AlertDescription>
                  </Alert>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowArchiveDialog(true)}
                    disabled={isPending}
                    data-testid="button-archive"
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    {account?.archivedAt ? "Restore Account" : "Archive Account"}
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            )}
            </div>
            )}

            {isEdit && activeTab === "deals" && (
              <div className="space-y-4 mt-4" data-testid="tab-content-deals">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium">Deals</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDealFormModal(true)}
                    data-testid="button-add-deal"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Deal
                  </Button>
                </div>

                {isLoadingDeals ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : deals.length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    <p>No deals for this account</p>
                    <p className="text-xs mt-2">Click "Add Deal" to create one</p>
                  </div>
                ) : (
                  <div className="border rounded-md">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-2 text-sm font-medium">Deal Name</th>
                          <th className="text-left p-2 text-sm font-medium">Stage</th>
                          <th className="text-left p-2 text-sm font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deals.map((deal) => (
                          <tr key={deal.id} className="border-b last:border-0">
                            <td className="p-2 text-sm">{deal.dealName}</td>
                            <td className="p-2 text-sm capitalize">{deal.transactionStage}</td>
                            <td className="p-2 text-sm">
                              {deal.amountRequested
                                ? new Intl.NumberFormat("en-AU", {
                                    style: "currency",
                                    currency: "AUD",
                                    minimumFractionDigits: 0,
                                  }).format(parseFloat(deal.amountRequested))
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 mt-6">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="button-save-account"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update" : "Create"} Account
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Create Client Group Dialog */}
      <Dialog open={showCreateGroupDialog} onOpenChange={setShowCreateGroupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Client Group</DialogTitle>
            <DialogDescription>
              Create a new client group and it will be automatically selected
            </DialogDescription>
          </DialogHeader>

          <Form {...createGroupForm}>
            <form onSubmit={createGroupForm.handleSubmit((data) => createGroupMutation.mutate(data))} className="space-y-4">
              <FormField
                control={createGroupForm.control}
                name="groupName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., ABC Financial Group"
                        data-testid="input-group-name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateGroupDialog(false)}
                  disabled={createGroupMutation.isPending}
                  data-testid="button-cancel-create-group"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createGroupMutation.isPending} data-testid="button-save-create-group">
                  {createGroupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Group
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Create Partner Dialog */}
      <PartnerFormModal
        open={showCreatePartnerDialog}
        onOpenChange={setShowCreatePartnerDialog}
        onPartnerCreated={handlePartnerCreated}
      />

      {/* Create Contact Dialog (for Individual Trustee) */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        defaultClientGroupId={form.watch("clientGroupId")}
        onContactCreated={handleContactCreated}
      />

      {/* Create Trustee Account Dialog (for Corporate Trustee) */}
      {!disableNesting && (
        <AccountFormModal
          open={showCreateTrusteeAccountDialog}
          onOpenChange={setShowCreateTrusteeAccountDialog}
          defaultClientGroupId={form.watch("clientGroupId")}
          defaultEntityType="company"
          onAccountCreated={handleTrusteeAccountCreated}
          disableNesting={true}
        />
      )}

      {/* Archive confirmation */}
    <CoreArchiveDialog entity="account" record={account} open={showArchiveDialog}
      onOpenChange={setShowArchiveDialog} onPendingChange={setArchivePending} onSuccess={() => onOpenChange(false)} />

      {/* Deal Form Modal - F007: AC007/AC008 */}
      {isEdit && account && (
        <DealFormModal
          open={showDealFormModal}
          onOpenChange={(open) => {
            setShowDealFormModal(open);
            // Refresh deals when modal closes
            if (!open) {
              queryClient.invalidateQueries({ queryKey: ['/api/crm/deals', { applicantId: account.id, applicantType: 'account' }] });
            }
          }}
          applicantId={account.id}
          applicantType="account"
          contextClientGroupId={form.watch("clientGroupId")}
        />
      )}
    </Dialog>
  );
}
